import { useEffect, useMemo, useState } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input, Button } from "@heroui/react";

const USERNAME_RE = /^[a-z0-9_]{3,20}$/i;

export default function ChooseUsernameModal({
  isOpen,
  onClose,
  onSave,           // async (username) => void
  checkAvailable,   // async (username) => boolean
  defaultSuggestion // optional string
}) {
  const [username, setUsername] = useState(defaultSuggestion || "");
  const [status, setStatus] = useState({ valid: false, msg: "", color: "default" });
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setUsername(defaultSuggestion || "");
      setStatus({ valid: false, msg: "", color: "default" });
    }
  }, [isOpen, defaultSuggestion]);

  const canFormat = useMemo(() => USERNAME_RE.test(username), [username]);

  const handleCheck = async () => {
    if (!canFormat) {
      setStatus({ valid: false, msg: "3–20 chars, letters/numbers/underscores only.", color: "danger" });
      return;
    }
    setChecking(true);
    try {
      const ok = await checkAvailable(username.trim());
      setStatus({
        valid: ok,
        msg: ok ? "Nice! That username is available." : "That username is taken.",
        color: ok ? "success" : "danger"
      });
    } finally {
      setChecking(false);
    }
  };

  const handleSave = async () => {
    if (!status.valid) return;
    setSaving(true);
    try {
      await onSave(username.trim());
      onClose();
    } catch (e) {
      setStatus({ valid: false, msg: e?.message || "Could not save username.", color: "danger" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {}} backdrop="blur" classNames={{
      wrapper: "z-[1010] backdrop-blur",
      base: "bg-gray-900 text-white"
    }}>
      <ModalContent>
        <ModalHeader>Choose your username</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-400">
            Pick a unique handle for ArtParcel. You can't change it later.
          </p>
          <Input
            label="Username"
            variant="bordered"
            value={username}
            onValueChange={setUsername}
            description={status.msg}
            color={status.color}
            isInvalid={status.color === "danger"}
            spellCheck={false}
            autoFocus
          />
          <div className="flex gap-2">
            <Button onPress={handleCheck} isLoading={checking} variant="flat">Check availability</Button>
            {status.valid && <span className="text-green-400 text-xs self-center">Available ✓</span>}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onPress={handleSave} color="primary" isDisabled={!status.valid} isLoading={saving}>
            Save username
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
